(() => {
  'use strict';

  const qs = (selector, root = document) => root.querySelector(selector);
  const qsa = (selector, root = document) => Array.from(root.querySelectorAll(selector));
  const normalise = (value) => String(value == null ? '' : value).trim();
  const lower = (value) => normalise(value).toLowerCase();

  const parseJson = (node) => {
    if (!node) return null;
    try { return JSON.parse(node.textContent); } catch (e) { return null; }
  };

  const config = parseJson(qs('[data-oss-product-config]')) || {};
  const product = parseJson(qs('[data-product-json]')) || null;
  const tags = Array.isArray(config.tags) ? config.tags.map(lower) : [];
  const productType = lower(config.type || (product && product.type));
  const isJersey = productType.includes('jersey') || tags.includes('jersey') || tags.includes('jerseys') || tags.includes('football jersey');
  if (!isJersey) return;

  const personalizationConfig = config.personalization || {};
  const personalizationPrice = Math.max(0, Number(personalizationConfig.price || 20));
  const addonVariantId = Number(personalizationConfig.addonVariantId || 0);
  const currency = config.currency || 'USD';
  const managedPropertyNames = new Set(['Personalisierung', 'Name', 'Nummer', 'Personalisierungs-Aufpreis']);

  const formatMoney = (amount) => {
    const number = Number(amount || 0);
    try {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(number);
    } catch (e) {
      return `$${number.toFixed(2)}`;
    }
  };

  const getSizeSource = () => qsa('.option-selector[data-option]').find((element) => {
    const option = lower(element.dataset.option);
    return ['size', 'größe', 'groesse', 'talla'].includes(option);
  }) || null;

  const getSizeSourceNodes = (source = getSizeSource()) => {
    if (!source) return [];
    const dropdown = qsa('.custom-select__option[data-value]', source).filter((node) => normalise(node.dataset.value));
    if (dropdown.length) return dropdown;
    return qsa('input.opt-btn, input[type="radio"]', source).filter((node) => normalise(node.value));
  };

  const sourceValue = (node) => normalise(node && (node.dataset && node.dataset.value ? node.dataset.value : node.value || node.textContent));
  const sourceSelected = (node) => {
    if (!node) return false;
    if (node.matches && node.matches('input')) return !!node.checked;
    return node.getAttribute('aria-selected') === 'true' || node.classList.contains('selected') || node.classList.contains('is-selected');
  };
  const sourceUnavailable = (node) => {
    if (!node) return false;
    return !!node.disabled || node.getAttribute('aria-disabled') === 'true' || node.classList.contains('is-unavailable') || node.classList.contains('opt-unavailable') || node.classList.contains('unavailable');
  };

  const getProductForm = () => {
    const button = qs('button[data-add-to-cart]');
    return button && button.closest('form');
  };

  const currentVariant = () => {
    if (!product || !Array.isArray(product.variants)) return null;
    const form = getProductForm();
    const input = form && qs('input[name="id"]', form);
    const id = input ? String(input.value) : '';
    return product.variants.find((variant) => String(variant.id) === id) || product.variants[0] || null;
  };

  const basePrice = () => {
    const variant = currentVariant();
    if (!variant) return 0;
    const cents = Number(variant.price || 0);
    return Number.isFinite(cents) ? cents / 100 : 0;
  };

  const parsePlayers = () => {
    const source = Array.isArray(personalizationConfig.players) ? personalizationConfig.players : [];
    return source.map((item) => {
      if (item && typeof item === 'object') {
        const name = normalise(item.name);
        const number = normalise(item.number);
        return name && number !== '' ? { name, number } : null;
      }
      const raw = normalise(item);
      if (!raw) return null;
      const split = raw.split('|');
      if (split.length < 2) return null;
      const number = normalise(split.pop());
      const name = normalise(split.join('|'));
      return name && number !== '' ? { name, number } : null;
    }).filter(Boolean);
  };

  const players = parsePlayers();
  const patchState = (Array.isArray(config.patches) ? config.patches : []).filter((patch) => patch && normalise(patch.name)).slice(0, 2).map((patch, index) => ({
    index,
    name: normalise(patch.name),
    price: Math.max(0, Number(patch.price || 0)),
    checked: false
  }));
  patchState.forEach((patch) => {
    managedPropertyNames.add(patch.name);
    managedPropertyNames.add(`${patch.name} surcharge`);
  });

  const personalization = {
    mode: '',
    playerIndex: -1,
    customName: '',
    customNumber: ''
  };

  const personalizationRequested = () => personalization.mode === 'player' || personalization.mode === 'custom';
  const personalizationValid = () => {
    if (personalization.mode === 'player') return personalization.playerIndex >= 0 && !!players[personalization.playerIndex];
    if (personalization.mode === 'custom') {
      const name = normalise(personalization.customName);
      const number = normalise(personalization.customNumber);
      if (!name || name.length > 10 || number === '') return false;
      const parsed = Number(number);
      return Number.isInteger(parsed) && parsed >= 0 && parsed <= 99;
    }
    return false;
  };

  const personalizationLabel = () => {
    if (personalization.mode === 'player') {
      const player = players[personalization.playerIndex];
      return player ? `${player.name} – ${player.number}` : '';
    }
    if (personalization.mode === 'custom') {
      const name = normalise(personalization.customName);
      const number = normalise(personalization.customNumber);
      if (!name && number === '') return 'Eigener Name';
      return `${name || 'Eigener Name'}${number !== '' ? ` – ${number}` : ''}`;
    }
    return '';
  };

  let sizePicker = null;
  let patchPanel = null;
  let personalizationBox = null;
  let totalBox = null;
  let mutationTimer = null;

  const ensureHiddenProperty = (form, name, value, enabled) => {
    if (!form || !name) return;
    const matching = qsa('input[type="hidden"][name^="properties["]', form).filter((input) => input.name === `properties[${name}]`);
    matching.forEach((input) => {
      if (input.dataset.ossR3Property !== '1') input.remove();
    });
    let input = qsa('input[type="hidden"][data-oss-r3-property="1"]', form).find((candidate) => candidate.name === `properties[${name}]`);
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = `properties[${name}]`;
      input.dataset.ossR3Property = '1';
      form.appendChild(input);
    }
    input.value = value;
    input.disabled = !enabled;
  };

  const syncLineItemProperties = () => {
    const form = getProductForm();
    if (!form) return;

    qsa('input[type="hidden"][name^="properties["]', form).forEach((input) => {
      const match = input.name.match(/^properties\[(.*)\]$/);
      if (match && managedPropertyNames.has(match[1]) && input.dataset.ossR3Property !== '1') input.remove();
    });

    patchState.forEach((patch) => {
      ensureHiddenProperty(form, patch.name, 'Yes', patch.checked);
      ensureHiddenProperty(form, `${patch.name} surcharge`, patch.price > 0 ? formatMoney(patch.price) : 'Included / not configured', patch.checked);
    });

    const requested = personalizationRequested();
    ensureHiddenProperty(form, 'Personalisierung', personalizationLabel(), requested);
    ensureHiddenProperty(form, 'Name', personalization.mode === 'custom' ? normalise(personalization.customName) : '', personalization.mode === 'custom' && requested);
    ensureHiddenProperty(form, 'Nummer', personalization.mode === 'custom' ? normalise(personalization.customNumber) : '', personalization.mode === 'custom' && requested);
    ensureHiddenProperty(form, 'Personalisierungs-Aufpreis', formatMoney(personalizationPrice), requested);
  };

  const patchExtras = () => patchState.reduce((sum, patch) => sum + (patch.checked ? patch.price : 0), 0);
  const displayedTotal = () => basePrice() + patchExtras() + (personalizationRequested() ? personalizationPrice : 0);

  const updateTotal = () => {
    if (!totalBox) return;
    const total = qs('[data-oss-r3-total]', totalBox);
    const breakdown = qs('[data-oss-r3-breakdown]', totalBox);
    if (total) total.textContent = formatMoney(displayedTotal());

    const parts = [];
    if (patchExtras() > 0) parts.push(`Patches +${formatMoney(patchExtras())}`);
    if (personalizationRequested()) parts.push(`Personalisierung +${formatMoney(personalizationPrice)}`);
    if (breakdown) {
      breakdown.textContent = parts.join(' · ');
      breakdown.hidden = parts.length === 0;
    }

    document.body.classList.toggle('oss-personalization-active', personalizationRequested());
    const priceNote = qs('[data-oss-personalization-price-note]');
    if (priceNote) priceNote.hidden = !personalizationRequested();
  };

  const syncSizePicker = () => {
    if (!sizePicker) return;
    const source = getSizeSource();
    const nodes = getSizeSourceNodes(source);
    const buttons = qsa('.oss-size-btn[data-oss-r3-size]', sizePicker);
    buttons.forEach((button) => {
      const value = normalise(button.dataset.ossR3Size);
      const node = nodes.find((candidate) => sourceValue(candidate) === value);
      const selected = sourceSelected(node);
      const unavailable = sourceUnavailable(node);
      button.classList.toggle('is-selected', selected);
      button.classList.toggle('is-unavailable', unavailable);
      button.disabled = unavailable;
      button.setAttribute('aria-pressed', selected ? 'true' : 'false');
    });
    syncLineItemProperties();
    updateTotal();
  };

  const selectUnderlyingSize = (value) => {
    const source = getSizeSource();
    const nodes = getSizeSourceNodes(source);
    const node = nodes.find((candidate) => sourceValue(candidate) === value);
    if (!node || sourceUnavailable(node)) return;

    if (node.matches && node.matches('input')) {
      const label = node.id ? qs(`label[for="${CSS.escape(node.id)}"]`, source) : null;
      if (label) label.click(); else node.click();
    } else {
      node.click();
    }

    window.setTimeout(syncSizePicker, 0);
    window.setTimeout(syncSizePicker, 90);
    window.setTimeout(syncSizePicker, 260);
  };

  const buildStableSizePicker = () => {
    const optionsBlock = qs('.product-detail__options');
    const source = getSizeSource();
    if (!optionsBlock || !source) return;
    const values = getSizeSourceNodes(source).map(sourceValue).filter(Boolean);
    if (!values.length) return;

    const old = qs('.oss-size-picker');
    if (old) old.remove();

    const picker = document.createElement('div');
    picker.className = 'oss-size-picker oss-size-picker--r3';
    picker.dataset.ossR3 = '1';

    const head = document.createElement('div');
    head.className = 'oss-size-head';
    const label = document.createElement('div');
    label.className = 'oss-size-label';
    label.textContent = 'Size';
    head.appendChild(label);

    const existingGuide = qs('.js-size-chart-open', optionsBlock) || qs('.js-size-chart-open');
    if (existingGuide || normalise(config.sizeGuideUrl)) {
      const guide = document.createElement('button');
      guide.type = 'button';
      guide.className = 'oss-size-guide';
      guide.textContent = 'Size Guide';
      guide.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        const currentGuide = qs('.js-size-chart-open', qs('.product-detail__options') || document) || qs('.js-size-chart-open');
        if (currentGuide) currentGuide.click();
        else if (normalise(config.sizeGuideUrl)) window.location.href = config.sizeGuideUrl;
      });
      head.appendChild(guide);
    }

    const grid = document.createElement('div');
    grid.className = 'oss-size-grid';
    grid.setAttribute('role', 'group');
    grid.setAttribute('aria-label', 'Size');

    values.forEach((value) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'oss-size-btn';
      button.dataset.ossR3Size = value;
      button.textContent = value;
      button.setAttribute('aria-pressed', 'false');
      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        selectUnderlyingSize(value);
      });
      grid.appendChild(button);
    });

    picker.append(head, grid);
    optionsBlock.insertAdjacentElement('beforebegin', picker);
    sizePicker = picker;
    syncSizePicker();
  };

  const patchIcon = (index) => index === 0
    ? '<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="8" fill="none" stroke="currentColor" stroke-width="1.7"/><path d="m12 5 1.7 4 4.3.3-3.3 2.8 1 4.2-3.7-2.2-3.7 2.2 1-4.2L6 9.3 10.3 9 12 5Z" fill="none" stroke="currentColor" stroke-width="1.3"/></svg>'
    : '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3 19 6v5c0 4.8-2.8 8-7 10-4.2-2-7-5.2-7-10V6l7-3Z" fill="none" stroke="currentColor" stroke-width="1.7"/><path d="m9 12 2 2 4-4" fill="none" stroke="currentColor" stroke-width="1.7"/></svg>';

  const buildStablePatchPanel = (anchor) => {
    const old = qs('.oss-patch-panel');
    if (old) old.remove();
    if (!anchor || !patchState.length) return null;

    const panel = document.createElement('div');
    panel.className = 'oss-patch-panel oss-patch-panel--r3';
    panel.dataset.ossR3 = '1';

    const heading = document.createElement('div');
    heading.className = 'oss-patch-heading';
    heading.textContent = 'Patches';
    panel.appendChild(heading);

    patchState.forEach((patch) => {
      const row = document.createElement('div');
      row.className = 'oss-patch-row';

      const left = document.createElement('div');
      left.className = 'oss-patch-left';
      const icon = document.createElement('span');
      icon.className = 'oss-patch-icon';
      icon.innerHTML = patchIcon(patch.index);
      const copy = document.createElement('span');
      copy.className = 'oss-patch-copy';
      const strong = document.createElement('strong');
      strong.textContent = patch.name;
      copy.appendChild(strong);
      if (patch.price > 0) {
        const small = document.createElement('small');
        small.textContent = `+${formatMoney(patch.price)}`;
        copy.appendChild(small);
      }
      left.append(icon, copy);

      const id = `oss-r3-patch-${patch.index}-${String(product && product.id || 'product').replace(/\D/g, '')}`;
      const switchLabel = document.createElement('label');
      switchLabel.className = 'oss-switch';
      switchLabel.setAttribute('for', id);
      switchLabel.addEventListener('click', (event) => event.stopPropagation());

      const checkbox = document.createElement('input');
      checkbox.type = 'checkbox';
      checkbox.id = id;
      checkbox.checked = patch.checked;
      checkbox.setAttribute('role', 'switch');
      checkbox.setAttribute('aria-label', patch.name);
      checkbox.addEventListener('click', (event) => event.stopPropagation());
      checkbox.addEventListener('change', () => {
        patch.checked = checkbox.checked;
        syncLineItemProperties();
        updateTotal();
      });

      const track = document.createElement('span');
      track.className = 'oss-switch-track';
      switchLabel.append(checkbox, track);
      row.append(left, switchLabel);
      panel.appendChild(row);
    });

    anchor.insertAdjacentElement('afterend', panel);
    patchPanel = panel;
    return panel;
  };

  const buildPersonalization = (anchor) => {
    if (!anchor) return null;
    const old = qs('.oss-personalization');
    if (old) old.remove();

    const wrap = document.createElement('div');
    wrap.className = 'oss-personalization';
    wrap.dataset.ossR3 = '1';

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'oss-personalize-button';
    button.textContent = 'Personalisieren';
    button.setAttribute('aria-expanded', 'false');
    button.setAttribute('aria-controls', 'oss-r3-personalization-panel');

    const panel = document.createElement('div');
    panel.className = 'oss-personalization-panel';
    panel.id = 'oss-r3-personalization-panel';
    panel.hidden = true;

    const header = document.createElement('div');
    header.className = 'oss-personalization-header';
    const title = document.createElement('div');
    title.className = 'oss-personalization-title';
    title.textContent = 'Spielerliste';
    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'oss-personalization-close';
    close.setAttribute('aria-label', 'Personalisierung schließen');
    close.textContent = '×';
    header.append(title, close);

    const field = document.createElement('div');
    field.className = 'oss-personalization-field';
    const selectLabel = document.createElement('label');
    selectLabel.className = 'oss-personalization-label';
    selectLabel.setAttribute('for', 'oss-r3-player-select');
    selectLabel.textContent = 'Spieler:in auswählen';
    const select = document.createElement('select');
    select.id = 'oss-r3-player-select';
    select.className = 'oss-personalization-select';
    const placeholder = document.createElement('option');
    placeholder.value = '';
    placeholder.textContent = 'Spieler:in auswählen';
    select.appendChild(placeholder);
    players.forEach((player, index) => {
      const option = document.createElement('option');
      option.value = `player:${index}`;
      option.textContent = `${player.name} – ${player.number}`;
      select.appendChild(option);
    });
    const customOption = document.createElement('option');
    customOption.value = 'custom';
    customOption.textContent = 'Eigener Name';
    select.appendChild(customOption);
    field.append(selectLabel, select);

    const customFields = document.createElement('div');
    customFields.className = 'oss-custom-personalization';
    customFields.hidden = true;

    const nameField = document.createElement('div');
    nameField.className = 'oss-personalization-field';
    const nameHead = document.createElement('div');
    nameHead.className = 'oss-personalization-label-row';
    const nameLabel = document.createElement('label');
    nameLabel.className = 'oss-personalization-label';
    nameLabel.setAttribute('for', 'oss-r3-custom-name');
    nameLabel.textContent = 'Name';
    const counter = document.createElement('span');
    counter.className = 'oss-character-counter';
    counter.textContent = '0/10';
    nameHead.append(nameLabel, counter);
    const nameInput = document.createElement('input');
    nameInput.id = 'oss-r3-custom-name';
    nameInput.className = 'oss-personalization-input';
    nameInput.type = 'text';
    nameInput.maxLength = 10;
    nameInput.autocomplete = 'off';
    nameInput.placeholder = 'Max. 10 Zeichen';
    nameField.append(nameHead, nameInput);

    const numberField = document.createElement('div');
    numberField.className = 'oss-personalization-field';
    const numberLabel = document.createElement('label');
    numberLabel.className = 'oss-personalization-label';
    numberLabel.setAttribute('for', 'oss-r3-custom-number');
    numberLabel.textContent = 'Nummer';
    const numberInput = document.createElement('input');
    numberInput.id = 'oss-r3-custom-number';
    numberInput.className = 'oss-personalization-input';
    numberInput.type = 'number';
    numberInput.min = '0';
    numberInput.max = '99';
    numberInput.step = '1';
    numberInput.inputMode = 'numeric';
    numberInput.placeholder = '0–99';
    numberField.append(numberLabel, numberInput);

    customFields.append(nameField, numberField);

    const status = document.createElement('div');
    status.className = 'oss-personalization-status';
    status.setAttribute('aria-live', 'polite');
    status.hidden = true;
    status.innerHTML = `<span>Personalisierungs-Aufpreis</span><strong>+${formatMoney(personalizationPrice)}</strong>`;
    status.dataset.ossPersonalizationPriceNote = '1';

    const error = document.createElement('div');
    error.className = 'oss-personalization-error';
    error.setAttribute('role', 'alert');
    error.hidden = true;

    const togglePanel = (force) => {
      const open = typeof force === 'boolean' ? force : panel.hidden;
      panel.hidden = !open;
      button.setAttribute('aria-expanded', open ? 'true' : 'false');
      wrap.classList.toggle('is-open', open);
      if (open) window.setTimeout(() => select.focus({ preventScroll: true }), 0);
    };

    button.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      togglePanel();
    });
    close.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      togglePanel(false);
      button.focus({ preventScroll: true });
    });

    select.addEventListener('change', () => {
      error.hidden = true;
      const value = select.value;
      if (value.startsWith('player:')) {
        const index = Number(value.split(':')[1]);
        personalization.mode = 'player';
        personalization.playerIndex = Number.isInteger(index) ? index : -1;
        customFields.hidden = true;
      } else if (value === 'custom') {
        personalization.mode = 'custom';
        personalization.playerIndex = -1;
        customFields.hidden = false;
        window.setTimeout(() => nameInput.focus({ preventScroll: true }), 0);
      } else {
        personalization.mode = '';
        personalization.playerIndex = -1;
        customFields.hidden = true;
      }
      syncLineItemProperties();
      updateTotal();
    });

    nameInput.addEventListener('input', () => {
      personalization.customName = nameInput.value.slice(0, 10);
      if (nameInput.value !== personalization.customName) nameInput.value = personalization.customName;
      counter.textContent = `${personalization.customName.length}/10`;
      error.hidden = true;
      syncLineItemProperties();
      updateTotal();
    });

    numberInput.addEventListener('input', () => {
      if (numberInput.value !== '') {
        let value = Number(numberInput.value);
        if (Number.isFinite(value)) {
          value = Math.max(0, Math.min(99, Math.trunc(value)));
          if (String(value) !== numberInput.value) numberInput.value = String(value);
        }
      }
      personalization.customNumber = numberInput.value;
      error.hidden = true;
      syncLineItemProperties();
      updateTotal();
    });

    panel.append(header, field, customFields, status, error);
    wrap.append(button, panel);
    anchor.insertAdjacentElement('afterend', wrap);
    personalizationBox = wrap;
    return wrap;
  };

  const buildTotal = (anchor) => {
    if (!anchor) return null;
    const old = qs('.oss-order-total');
    if (old) old.remove();
    const box = document.createElement('div');
    box.className = 'oss-order-total';
    box.innerHTML = '<div class="oss-order-total__row"><span>Gesamtpreis</span><strong data-oss-r3-total></strong></div><div class="oss-order-total__breakdown" data-oss-r3-breakdown hidden></div>';
    anchor.insertAdjacentElement('afterend', box);
    totalBox = box;
    updateTotal();
    return box;
  };

  const moveProductDetails = (anchor) => {
    const details = qs('.oss-product-details');
    if (!details || !anchor) return;
    anchor.insertAdjacentElement('afterend', details);
  };

  const showPersonalizationError = (message) => {
    const error = personalizationBox && qs('.oss-personalization-error', personalizationBox);
    if (error) {
      error.textContent = message;
      error.hidden = false;
    }
    if (personalizationBox) {
      const panel = qs('.oss-personalization-panel', personalizationBox);
      const button = qs('.oss-personalize-button', personalizationBox);
      if (panel && panel.hidden) {
        panel.hidden = false;
        personalizationBox.classList.add('is-open');
        if (button) button.setAttribute('aria-expanded', 'true');
      }
    }
  };

  const jerseyProperties = () => {
    const properties = {};
    patchState.forEach((patch) => {
      if (!patch.checked) return;
      properties[patch.name] = 'Yes';
      properties[`${patch.name} surcharge`] = patch.price > 0 ? formatMoney(patch.price) : 'Included / not configured';
    });

    if (personalizationRequested()) {
      properties.Personalisierung = personalizationLabel();
      properties['Personalisierungs-Aufpreis'] = formatMoney(personalizationPrice);
      if (personalization.mode === 'custom') {
        properties.Name = normalise(personalization.customName);
        properties.Nummer = normalise(personalization.customNumber);
      }
    }
    return properties;
  };

  const quantityFromForm = (form) => {
    const field = qs('[name="quantity"]', form);
    const value = field ? Number(field.value) : 1;
    return Number.isFinite(value) && value > 0 ? Math.max(1, Math.trunc(value)) : 1;
  };

  const addPersonalizedItemToCart = async (form) => {
    const variantInput = qs('input[name="id"]', form);
    const mainVariantId = variantInput ? Number(variantInput.value) : 0;
    if (!mainVariantId || !addonVariantId) throw new Error('Variant unavailable');

    const quantity = quantityFromForm(form);
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const response = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({
        items: [
          {
            id: mainVariantId,
            quantity,
            properties: jerseyProperties()
          },
          {
            id: addonVariantId,
            quantity,
            properties: {
              '_OSS personalization for': normalise(config.title || (product && product.title)),
              '_OSS personalization selection': personalizationLabel()
            }
          }
        ]
      })
    });
    if (!response.ok) {
      let message = 'Could not add personalized item to cart.';
      try {
        const data = await response.json();
        message = data.description || data.message || message;
      } catch (e) {}
      throw new Error(message);
    }
    window.location.assign(`${root}cart`);
  };

  const installSubmitGuard = () => {
    document.addEventListener('submit', (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !qs('button[data-add-to-cart]', form)) return;
      if (!personalizationRequested()) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      if (!personalizationValid()) {
        if (personalization.mode === 'custom') {
          const name = normalise(personalization.customName);
          const number = normalise(personalization.customNumber);
          if (!name) {
            showPersonalizationError('Bitte einen Namen eingeben (max. 10 Zeichen).');
            const input = personalizationBox && qs('#oss-r3-custom-name', personalizationBox);
            if (input) input.focus();
          } else if (number === '' || !Number.isInteger(Number(number)) || Number(number) < 0 || Number(number) > 99) {
            showPersonalizationError('Bitte eine Nummer zwischen 0 und 99 eingeben.');
            const input = personalizationBox && qs('#oss-r3-custom-number', personalizationBox);
            if (input) input.focus();
          }
        } else {
          showPersonalizationError('Bitte eine Spieler:in auswählen.');
        }
        return;
      }

      const button = qs('button[data-add-to-cart]', form);
      const originalText = button && button.textContent;
      if (button) {
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
        button.textContent = 'Wird hinzugefügt…';
      }

      addPersonalizedItemToCart(form).catch((error) => {
        if (button) {
          button.disabled = false;
          button.removeAttribute('aria-busy');
          button.textContent = originalText || 'ADD TO CART';
        }
        showPersonalizationError(error && error.message ? error.message : 'Beim Hinzufügen zum Warenkorb ist ein Fehler aufgetreten.');
      });
    }, true);
  };

  const installDynamicSync = () => {
    const detail = qs('.product-detail__detail');
    if (!detail) return;
    new MutationObserver((mutations) => {
      const relevant = mutations.some((mutation) => mutation.type === 'childList' || (mutation.type === 'attributes' && ['checked', 'aria-selected', 'aria-disabled', 'disabled', 'value', 'class'].includes(mutation.attributeName)));
      if (!relevant) return;
      window.clearTimeout(mutationTimer);
      mutationTimer = window.setTimeout(() => {
        syncSizePicker();
        syncLineItemProperties();
        updateTotal();
      }, 25);
    }).observe(detail, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['checked', 'aria-selected', 'aria-disabled', 'disabled', 'value', 'class']
    });

    document.addEventListener('change', (event) => {
      if (!event.target.closest('.product-detail')) return;
      window.setTimeout(() => {
        syncSizePicker();
        syncLineItemProperties();
        updateTotal();
      }, 0);
    });
  };

  const init = () => {
    const optionsBlock = qs('.product-detail__options');
    if (!optionsBlock) return;

    document.body.classList.add('oss-round3-ready');

    /* Remove round-2 UI that lived inside Shopify's dynamic variant block. */
    const oldSize = qs('.oss-size-picker');
    const oldPatch = qs('.oss-patch-panel');
    if (oldSize) oldSize.remove();
    if (oldPatch) oldPatch.remove();

    buildStableSizePicker();
    const personalizationUi = buildPersonalization(sizePicker);
    const patches = buildStablePatchPanel(personalizationUi || sizePicker);
    const total = buildTotal(patches || personalizationUi || sizePicker);
    moveProductDetails(total);

    syncLineItemProperties();
    updateTotal();
    installSubmitGuard();
    installDynamicSync();

    window.setTimeout(syncSizePicker, 120);
    window.setTimeout(syncSizePicker, 420);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();